import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  DateField,
  FormControl,
  Heading,
  Icon,
  IconSize,
  Text,
  useModalState,
  useSideSheetState,
} from '@bechtel/gds';
import '@bechtel/gds/dist/index.css';
import { displayErrorMessage, i18next as i18n, isValidStartDate, useAppUser } from '@bechtel/ht-clients-common';
import { ToolOrder, ToolOrderCatalog, ToolOrderStatus } from '@bechtel/ht-types';
import { format } from 'date-fns';
import { produce } from 'immer';
import { map, sumBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ErrorBoundary } from '~/components';
import { ActionContent } from '~/components/action-content';
import { Backdrop } from '~/components/backdrop';
import { CartHeader } from '~/components/cart-header';
import { CatalogInfo } from '~/components/catalog-info';
import { ImageFallback } from '~/components/image-fallback';
import { EditCountField } from '~/components/molecules/edit-count-field';
import { ErrorModal } from '~/components/molecules/error-modal';
import RestrictedLimitComponent from '~/components/molecules/restricted-limit';
import { NonCatalogImage, NonCatalogInfo } from '~/components/non-catalog/list-item';
import { useUpdateToolOrder, useUpdateToolOrderCatalog } from '~/hooks';
import { ActionButtonStyled, TertiaryButtonStyled } from '~/styles';
import { addDaysToDate } from '~/utilities';
import { CreateReservationSideSheetStyled } from '../styled';
import { ToolCatalogItemCommentDialog } from './comment-dialog';
import {
  CartHeaderContainerStyled,
  CartHeaderStyled,
  FinalizeSectionBodyStyled,
  FinalizeSectionHeaderStyled,
  FinalizeSectionStyled,
  FormControlStyled,
  InputTimeStyled,
  MyCartHeaderStyled,
  RemoveCartItemStyled,
  TimeSectionStyled,
  ToolCartItemStyled,
} from './styled';

export const MyCart: React.FC = () => {
  const { appUser } = useAppUser();
  const location = useLocation();
  const [cartErrorMessage, setCartErrorMessage] = useState('');
  const [hasError, setHasError] = useState<boolean>(false);
  const { close, visible } = useSideSheetState(true);
  const [toolOrder, setToolOrder] = useState<ToolOrder>(() => {
    try {
      return location.state?.toolOrder;
    } catch (error) {
      console.log(error);
      return;
    }
  });
  const [requiredDate, setRequiredDate] = useState(addDaysToDate(new Date(), 3, true));
  const [toolOrderCatalogItems, setToolOrderCatalogItems] = useState<ToolOrderCatalog[]>(
    toolOrder?.toolOrderCatalogs || []
  );
  const [requiredTime, setRequiredTime] = useState(format(new Date(), 'kk:mm'));
  const { visible: commentDialogVisible, open: openCommentDialog, close: closeCommentDialog } = useModalState(false);
  const [focusedCatalogItem, setFocusedCatalogItem] = useState<ToolOrderCatalog | null>(null);
  const { updateToolOrder: updateToolOrderMutation, loading: toolOrderUpdating } = useUpdateToolOrder();
  const { updateToolOrderCatalog } = useUpdateToolOrderCatalog();

  const navigate = useNavigate();

  const onAddMoreToolsOrClose = () => {
    navigate(`/reservations/draft/${toolOrder?.id}`, {
      state: { draftToolOrder: { ...toolOrder, toolOrderCatalogs: toolOrderCatalogItems } },
    });
    close();
  };
  const onOrderPlaced = (toolOrderTemp: ToolOrder) => {
    setToolOrder(toolOrderTemp);
    setToolOrderCatalogItems(toolOrderTemp?.toolOrderCatalogs || []);
    navigate(`/reservations/confirm`, { state: { toolOrder: toolOrderTemp } });
  };

  const updateToolOrderCatalogData = (toolOrderCatalogItem: ToolOrderCatalog, comments: string) => {
    const toolOrderCatalogId = toolOrderCatalogItem.id;
    updateToolOrderCatalog(
      {
        id: toolOrderCatalogId,
        requesterComments: comments,
        quantity: toolOrderCatalogItem.quantity,
        requesterCommentsBy: appUser?.bun,
      },
      (data) => {
        if (!data.errors) {
          onToolCartItemCommentsUpdate(toolOrderCatalogItem, comments);
        }
      },
      (error) => {
        setCartErrorMessage('Error while adding comments!Please try again.');
      }
    );
  };
  const hasCartChanges = useMemo(() => {
    if (!toolOrder) return true;
    if (
      (toolOrder &&
        toolOrder.toolOrderCatalogs &&
        toolOrder.toolOrderCatalogs.length !== toolOrderCatalogItems.length) ||
      sumBy(toolOrderCatalogItems, (catalogItem) => catalogItem.quantity) !==
        sumBy(toolOrder.toolOrderCatalogs, (toolOrderCatalog) => toolOrderCatalog.quantity)
    )
      return true;
    for (const cartItem of toolOrderCatalogItems) {
      const toolOrderCatalog = toolOrder?.toolOrderCatalogs?.find(
        (item) =>
          item.toolCatalogId === cartItem?.toolCatalog?.id || (!item.toolCatalogId && item.nonCatalogDescription)
      );
      if (toolOrderCatalog?.quantity !== cartItem.quantity) return true;
    }

    return false;
  }, [toolOrder, toolOrderCatalogItems]);

  const onToolCartItemEdit = useCallback((toolOrderCatalog: ToolOrderCatalog, count: number) => {
    setToolOrderCatalogItems(
      produce((draft) => {
        const index = draft.findIndex((item) => item.id === toolOrderCatalog.id);
        if (index > -1) {
          if (count) {
            draft[index].quantity = count;
            draft[index].requesterComments = toolOrderCatalog.requesterComments;
          } else draft.splice(index, 1);
        }
      })
    );
  }, []);
  const onToolCartItemCommentsUpdate = useCallback((toolOrderCatalog: ToolOrderCatalog, comments?: string) => {
    setToolOrderCatalogItems(
      produce((draft) => {
        const index = draft.findIndex((item) => item.id === toolOrderCatalog.id);
        if (index > -1) {
          draft[index].requesterComments = comments;
        }
      })
    );
  }, []);

  const placeOrder = () => {
    if (!toolOrder || !toolOrderCatalogItems?.length || !isValidStartDate(requiredDate)) return;
    updateToolOrderMutation(
      {
        id: toolOrder.id,
        status: ToolOrderStatus.PLACED,
        orderPlacedFor: toolOrder.orderPlacedFor, //TODO: Should be optional
        recipientId: toolOrder.recipientId,
        projectId: toolOrder.projectId, //TODO: should be optional
        requiredStartDate: format(requiredDate, 'yyyy-MM-dd') + 'T' + requiredTime + ':00.000Z',
        orderDate: new Date(),
        toolRoomId: toolOrder.toolRoomId,
        toolOrderCatalog: toolOrderCatalogItems?.map((toolOrderCatalog) => {
          return {
            id: toolOrderCatalog.id,
            quantity: toolOrderCatalog.quantity,
            requesterComments: toolOrderCatalog.requesterComments,
            toolCatalogId: toolOrderCatalog.toolCatalogId,
            nonCatalogDescription: toolOrderCatalog.nonCatalogDescription,
            nonCatalogReferenceUrl: toolOrderCatalog.nonCatalogReferenceUrl
              ? btoa(toolOrderCatalog.nonCatalogReferenceUrl)
              : toolOrderCatalog.nonCatalogReferenceUrl,
            nonCatalogSimilarToId: toolOrderCatalog.similarToolCatalog?.id,
            nonCatalogUomId: toolOrderCatalog.nonCatalogUomId,
          };
        }),
      },
      (data) => {
        if (data.updateToolOrder?.toolOrder) {
          setToolOrder(data.updateToolOrder?.toolOrder);
          onOrderPlaced(data.updateToolOrder?.toolOrder);
        } else if (data.updateToolOrder?.error) {
          setCartErrorMessage(i18n.t(data.updateToolOrder?.error));
        }
      },
      (error) => {
        setCartErrorMessage(i18n.t(error.message));
      },
    );
  };

  const updateOrder = (callBack) => {
    if (hasCartChanges && toolOrder && toolOrderCatalogItems?.length > 0) {
      updateToolOrderMutation(
        {
          id: toolOrder.id,
          status: ToolOrderStatus.DRAFT,
          toolRoomId: toolOrder.toolRoomId,
          orderPlacedFor: toolOrder.orderPlacedFor, //TODO: Should be optional
          recipientId: toolOrder.recipientId,
          projectId: toolOrder.projectId, //TODO: should be optional
          requiredStartDate: format(requiredDate, 'yyyy-MM-dd') + 'T' + requiredTime + ':00.000Z',
          toolOrderCatalog: toolOrderCatalogItems?.map((toolOrderCatalog) => {
            return {
              id: toolOrderCatalog.id,
              quantity: toolOrderCatalog.quantity,
              requesterComments: toolOrderCatalog.requesterComments,
              toolCatalogId: toolOrderCatalog.toolCatalogId,
              nonCatalogDescription: toolOrderCatalog.nonCatalogDescription,
              nonCatalogReferenceUrl: toolOrderCatalog.nonCatalogReferenceUrl
                ? btoa(toolOrderCatalog.nonCatalogReferenceUrl)
                : toolOrderCatalog.nonCatalogReferenceUrl,
              nonCatalogSimilarToId: toolOrderCatalog.similarToolCatalog?.id,
              nonCatalogUomId: toolOrderCatalog.nonCatalogUomId,
            };
          }),
        },
        (data) => {
          callBack && callBack(data?.updateToolOrder?.toolOrder);
        },
        (error) => {
          setCartErrorMessage(i18n.t(error.message));
        },
      );
    } else {
      callBack && callBack();
    }
  };

  const total = toolOrderCatalogItems.reduce((acc, item) => acc + item.quantity, 0);
  const excludeDates: any = [];
  const currentDate = new Date();

  useEffect(() => {
    const isValid = isValidStartDate(requiredDate);
    if (!isValid) {
      setCartErrorMessage(displayErrorMessage('The required date must be after 2 working days from now'));
    }
    setHasError(!isValid);
  }, [requiredDate]);

  while (currentDate.getDay() !== 6) {
    currentDate.setDate(currentDate.getDate() + 1);
  }

  while (excludeDates.length < 20) {
    excludeDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
    excludeDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 6);
  }
  const closeModal = () => {
    setCartErrorMessage('');
  };
  return (
    <ErrorBoundary>
      {toolOrderUpdating && <Backdrop></Backdrop>}
      <CreateReservationSideSheetStyled onDismiss={onAddMoreToolsOrClose} visible={visible}>
        {cartErrorMessage && (
          <ErrorModal close={closeModal} message={cartErrorMessage} title="Validation Message" closeText="Close" />
        )}
        <div style={{ height: '100%' }}>
          <MyCartHeaderStyled>
            <Box px={{ sm: '500' }} alignItems={{ sm: 'center' }} gap={{ sm: '600' }}>
              <Heading size="500" color="grey.1000">
                My Cart ({total})
              </Heading>
              <ActionButtonStyled
                size={ButtonSize.SMALL}
                appearance={ButtonAppearance.SECONDARY}
                onClick={() => updateOrder(onAddMoreToolsOrClose)}
              >
                <ActionContent icon="plus" label="Add More"></ActionContent>
              </ActionButtonStyled>
            </Box>
            <Box p={{ sm: '400' }}>
              <Button
                size={ButtonSize.SMALL}
                onClick={() => {
                  updateOrder(onAddMoreToolsOrClose);
                }}
                appearance={ButtonAppearance.TERTIARY}
              >
                <Icon icon="close" fillColor="blue.800" size={IconSize.LARGE} />
              </Button>
            </Box>
          </MyCartHeaderStyled>
          <Box style={{ height: '100%' }}>
            <CartHeaderContainerStyled flexDirection={{ sm: 'column' }}>
              <CartHeaderStyled>
                <CartHeader
                  toolOrderCatalogItems={map(toolOrderCatalogItems, (toolOrderCatalog) => {
                    return { toolCatalog: toolOrderCatalog.toolCatalog, quantity: toolOrderCatalog.quantity };
                  })}
                  onClearCart={() => {
                    toolOrderCatalogItems.forEach((toolOrderCatalogItem) => {
                      onToolCartItemEdit(toolOrderCatalogItem, 0);
                    });
                  }}
                ></CartHeader>
              </CartHeaderStyled>
              <div>
                {toolOrderCatalogItems?.map((toolOrderCatalogItem) => {
                  const toolCatalog = toolOrderCatalogItem.toolCatalog;
                  if (toolCatalog) {
                    return (
                      <ToolCartItemStyled
                        key={toolOrderCatalogItem.id}
                        onMouseOver={(event) => {
                          setFocusedCatalogItem(toolOrderCatalogItem);
                          event.stopPropagation();
                        }}
                      >
                        <Box
                          py={{ sm: '500' }}
                          justifyContent={{ sm: 'space-between' }}
                          style={{ flex: '1' }}
                          alignItems={{ sm: 'center' }}
                        >
                          <Box justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} mx={{ sm: '500' }}>
                            <ImageFallback
                              src={toolCatalog?.toolCatalogImageUrl}
                              fallbackName={toolCatalog?.toolCategory?.name}
                            />
                          </Box>
                          <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '30%' }}>
                            <CatalogInfo
                              barCode={toolCatalog && toolCatalog.bscnPartNumber ? toolCatalog.bscnPartNumber : ''}
                              description={toolCatalog && toolCatalog.name ? toolCatalog.name : ''}
                              isSerialized={
                                ['Individual', 'Equipment'].includes(toolCatalog?.inventoryType ?? '') ? true : false
                              }
                              uomAbbreviation={toolCatalog?.uom ? toolCatalog.uom.abbreviation : ''}
                            />
                            <RemoveCartItemStyled
                              onClick={() => {
                                onToolCartItemEdit(toolOrderCatalogItem, 0);
                              }}
                            >
                              <Text color="grey.500" size="300">
                                &#8211; Remove from Cart
                              </Text>
                            </RemoveCartItemStyled>
                          </Box>
                          <Box flexGrow={{ sm: '1' }}>
                            {toolOrderCatalogItem.requesterComments && (
                              <Box gap={{ sm: '300' }}>
                                <Icon icon="comment-lines" fillColor="primary.700" iconStyle="solid" />
                                <Text
                                  color="grey.600"
                                  size="400"
                                  style={{ fontStyle: 'italic' }}
                                  family="openSansCondensed"
                                >
                                  {toolOrderCatalogItem.requesterComments}
                                </Text>
                              </Box>
                            )}
                          </Box>
                          <Box px={{ sm: '500' }}>
                            {focusedCatalogItem?.id === toolOrderCatalogItem.id && (
                              <TertiaryButtonStyled
                                onClick={() => {
                                  openCommentDialog();
                                }}
                              >
                                {!toolOrderCatalogItem.requesterComments ? (
                                  <ActionContent label="Add Comments" icon="plus"></ActionContent>
                                ) : (
                                  <ActionContent label="Edit Comments" icon="edit"></ActionContent>
                                )}
                              </TertiaryButtonStyled>
                            )}
                          </Box>
                          <Box flexDirection={{ sm: 'column' }} gap={{ sm: '200' }} mr={{ sm: '600' }}>
                            <EditCountField
                              count={toolOrderCatalogItem.quantity}
                              onIncrement={() => {
                                onToolCartItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity + 1);
                              }}
                              plusBtnDisable={
                                toolCatalog.limitCount &&
                                toolCatalog.limitCount > 0 &&
                                toolOrderCatalogItem.quantity >= toolCatalog?.limitCount
                                  ? true
                                  : false
                              }
                              onDecrement={() => {
                                if (toolOrderCatalogItem.quantity > 0) {
                                  onToolCartItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity - 1);
                                }
                              }}
                              onCountTextChange={(quantity) => {
                                if (
                                  toolCatalog.limitCount &&
                                  toolCatalog.limitCount > 0 &&
                                  quantity > toolCatalog?.limitCount
                                ) {
                                  return;
                                }
                                onToolCartItemEdit(toolOrderCatalogItem, quantity);
                              }}
                            ></EditCountField>
                            <RestrictedLimitComponent
                              isLocked={toolCatalog?.isLocked}
                              limitCount={toolCatalog?.limitCount}
                            />
                          </Box>
                          {focusedCatalogItem?.id === toolOrderCatalogItem.id && visible && (
                            <ToolCatalogItemCommentDialog
                              onClose={closeCommentDialog}
                              visible={commentDialogVisible}
                              toolOrderCatalogItem={focusedCatalogItem}
                              onCommentUpdated={(toolCatalogItem, comments) => {
                                updateToolOrderCatalogData(toolCatalogItem, comments || '');
                              }}
                            />
                          )}
                        </Box>
                      </ToolCartItemStyled>
                    );
                  } else {
                    return (
                      <ToolCartItemStyled
                        key={toolOrderCatalogItem.id}
                        onMouseOver={(event) => {
                          setFocusedCatalogItem(toolOrderCatalogItem);
                          event.stopPropagation();
                        }}
                      >
                        <Box
                          px={{ sm: '500' }}
                          py={{ sm: '500' }}
                          justifyContent={{ sm: 'space-between' }}
                          style={{ flex: '1' }}
                          alignItems={{ sm: 'center' }}
                        >
                          <Box flexDirection={{ sm: 'column' }} style={{ flexBasis: '35%' }}>
                            <NonCatalogInfo
                              nonCatalogDescription={toolOrderCatalogItem.nonCatalogDescription || ''}
                              nonCatalogUomId={toolOrderCatalogItem?.nonCatalogUomId ?? 0}
                            >
                              <NonCatalogImage />
                            </NonCatalogInfo>
                            <Box justifyContent={{ sm: 'center' }} style={{ width: '242px' }}>
                              <RemoveCartItemStyled
                                onClick={() => {
                                  onToolCartItemEdit(toolOrderCatalogItem, 0);
                                }}
                              >
                                <Text color="grey.500" size="300">
                                  &#8211; Remove from Cart
                                </Text>
                              </RemoveCartItemStyled>
                            </Box>
                          </Box>
                          <Box mr={{ sm: '200' }}>
                            <EditCountField
                              count={toolOrderCatalogItem.quantity}
                              onIncrement={() => {
                                onToolCartItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity + 1);
                              }}
                              onDecrement={() => {
                                if (toolOrderCatalogItem.quantity > 0) {
                                  onToolCartItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity - 1);
                                }
                              }}
                              onCountTextChange={(quantity) => {
                                onToolCartItemEdit(toolOrderCatalogItem, quantity);
                              }}
                            ></EditCountField>
                          </Box>
                        </Box>
                      </ToolCartItemStyled>
                    );
                  }
                })}
              </div>
            </CartHeaderContainerStyled>
            <FinalizeSectionStyled pb={{ sm: '800' }}>
              <FinalizeSectionHeaderStyled>
                <Text size="400" weight="600" color="blue.800">
                  Select Date & Time
                </Text>
              </FinalizeSectionHeaderStyled>
              <FinalizeSectionBodyStyled>
                <FormControlStyled label="Required Date">
                  <DateField
                    selected={requiredDate}
                    minDate={addDaysToDate(new Date(), 2, true)}
                    onChange={(date) => setRequiredDate(date)}
                    showPopperArrow={false}
                    icon="calendar-alt"
                    iconStyle="duo"
                  />
                </FormControlStyled>
                <Text color="grey.500" size="300">
                  The required date must be after 2 working days from now.
                </Text>
                <TimeSectionStyled>
                  <FormControl label={'Required Time'} style={{ height: '40px' }}>
                    <InputTimeStyled
                      value={requiredTime}
                      onChange={(event) => {
                        setRequiredTime(event.target.value);
                      }}
                    />
                  </FormControl>
                </TimeSectionStyled>
              </FinalizeSectionBodyStyled>
              <Box px={{ sm: '500' }} py={{ sm: '300' }}>
                <Button disabled={!toolOrder || !toolOrderCatalogItems.length || hasError} onClick={placeOrder}>
                  <ActionContent icon="check" label="Reserve Tools" labelSize={'400'}></ActionContent>
                </Button>
              </Box>
            </FinalizeSectionStyled>
          </Box>
        </div>
      </CreateReservationSideSheetStyled>
    </ErrorBoundary>
  );
};
